import { cn } from "@/lib/utils";
import { Bell, ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

// Building blocks of the employee app mockup. They mirror the real mobile
// components (mobile-list-row, Badge, MobileHeader) so the section looks like
// the app. Text uses div/span on purpose: the global `p { color }` rule in
// globals.css would otherwise override the colors set here.

export type Tone =
  | "yellow"
  | "ac"
  | "night"
  | "green"
  | "warn"
  | "red"
  | "gray"
  | "neutral"
  | "amber";

const TONES: Record<Tone, string> = {
  yellow: "border-[#eab308] bg-[#fef9c3] text-[#ca8a04]",
  ac: "border-[#ffa514] bg-[#ffefd4] text-[#d97706]",
  night: "border-[#64748b] bg-[#64748b] text-[#e2e8f0]",
  green: "border-[#a8ffa8] bg-[#f0fff0] text-[#009900]",
  warn: "border-[#eab308] bg-[#fefce8] text-[#a16207]",
  red: "border-[#ef4444] bg-[#fee2e2] text-[#ef4444]",
  gray: "border-[#6b7280] bg-[#e5e7eb] text-[#6b7280]",
  neutral: "border-[#d1d5db] bg-[#f3f4f6] text-[#4b5563]",
  amber: "border-[#f59e0b] bg-[#fef3c7] text-[#d97706]",
};

export function IconChip({
  tone,
  children,
}: {
  tone: Tone;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "grid h-9 w-9 shrink-0 place-items-center rounded-lg border",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Badge({
  tone,
  pill = false,
  className,
  children,
}: {
  tone: Tone;
  pill?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap border px-2 py-0.5 text-xs font-semibold leading-4",
        pill ? "rounded-full" : "rounded-md",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Group({ children }: { children: ReactNode }) {
  return (
    <div className="divide-y divide-[#e5e7eb] overflow-hidden rounded-xl border border-[#e5e7eb] bg-white">
      {children}
    </div>
  );
}

export function ListRow({
  chip,
  title,
  titleSuffix,
  subtitle,
  trailing,
  chevron = false,
  muted = false,
}: {
  chip?: ReactNode;
  title: ReactNode;
  titleSuffix?: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3">
      {chip}
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-base font-semibold leading-none",
            muted ? "text-[#808080]" : "text-[#333]",
          )}
        >
          {title}
          {titleSuffix}
        </div>
        {subtitle && (
          <div className="mt-0.5 flex items-center gap-1 truncate text-sm text-[#808080]">
            {subtitle}
          </div>
        )}
      </div>
      {trailing}
      {chevron && (
        <ChevronRight size={16} className="shrink-0 text-[#808080]" aria-hidden />
      )}
    </div>
  );
}

export function SectionLabel({
  children,
  aside,
}: {
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between px-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[#808080]">
        {children}
      </span>
      {aside && <span className="text-xs text-[#808080]">{aside}</span>}
    </div>
  );
}

export function ScreenHeader({
  title,
  back = false,
  children,
}: {
  title: string;
  back?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="sticky top-0 z-[2] flex shrink-0 items-center gap-2 bg-[#f3f4f6] px-6 pb-2 pt-3">
      {back && (
        <span className="-ml-3 grid h-8 w-8 place-items-center text-[#808080]">
          <ChevronLeft size={20} aria-hidden />
        </span>
      )}
      <div
        className={cn(
          "flex-1 text-lg font-bold leading-7 text-[#333]",
          back && "-ml-1.5",
        )}
      >
        {title}
      </div>
      {children}
      <span className="grid h-8 w-8 place-items-center text-[#808080]">
        <Bell size={20} aria-hidden />
      </span>
    </div>
  );
}
